import { get, post } from '@/lib/api/client'
import {
  type Video,
  type VideoDetail,
  type VideoFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams,
  type GalleryFolder,
  type VideoFolderSearchResult
} from '@/lib/api/types'
import { createQueryString } from '@/utils/helpers/createQueryString'

export class VideoService {
  /**
   * Get all video folders (Public endpoint)
   * GET /folders/public/all?folderType=VIDEO
   */
  async getVideoFolders(): Promise<ApiResponse<GalleryFolder[]>> {
    return await get<ApiResponse<GalleryFolder[]>>(
      'folders/public/all?folderType=VIDEO'
    )
  }

  /**
   * Search video folders by query (Public endpoint)
   * GET /folders/public/search?query={query}
   * Note: This endpoint is not paginated and returns all matching folders
   */
  async searchVideoFolders(query: string, params: PaginationParams): Promise<ApiResponse<PaginatedResponse<VideoFolderSearchResult>>> {
    const queryString = createQueryString({...params, searchBy: query, folderType: 'VIDEO' })
    return await get<ApiResponse<PaginatedResponse<VideoFolderSearchResult>>>(
      `folders/public/search?${queryString}`
    )
  }

  /**
   * Filter videos with pagination (Public endpoint)
   * POST /videos/public/filter?size={size}&page={page}
   */
  async filterVideos(
    params: PaginationParams = { page: 1, size: 10 },
    filters: VideoFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Video>>> {
    
    // Build query string
    const queryParams = createQueryString(params)

    const endpoint = `videos/public/filter?${queryParams}`

    const body = {...filters}

    return await post<ApiResponse<PaginatedResponse<Video>>>(
      endpoint,
      body
    )
  }

  /**
   * Search videos by query (Public endpoint)
   * GET /videos/public/search?query={query}
   */
  async searchVideos(query: {searchTerm: string; folderId: string}, params: PaginationParams): Promise<ApiResponse<PaginatedResponse<Video>>> {
    const queryString = createQueryString({...params, searchBy: query.searchTerm, folderId: query.folderId })
    return await get<ApiResponse<PaginatedResponse<Video>>>(
      `videos/public/search?${queryString}`
    )
  }

  /**
   * Get a single video by ID (Public endpoint)
   * GET /videos/public/{videoUploadId}
   */

  async getVideoById(videoUploadId: string): Promise<ApiResponse<VideoDetail>> {
    return await get<ApiResponse<VideoDetail>>(
      `videos/public/${videoUploadId}`
    )
  }

  /**
   * Get home page highlights
   * GET /videos/public/highlights?type=HOME_PAGE_IS_KEY_HIGHLIGHT
   */
  async getHomePageHighlights(): Promise<ApiResponse<Video[]>> {
    return await get<ApiResponse<Video[]>>(
      'videos/public/highlights?type=HOME_PAGE_IS_KEY_HIGHLIGHT'
    )
  }
}

// Export singleton instance
export const videoService = new VideoService()

// Export default
export default videoService

